import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Network, Pencil, Settings } from 'lucide-react';
import { useIsInDraftMode } from '../hooks/useDraftMode';
import { useBuildTree, usePipelineNodes } from '../hooks/usePipelineNodes';
import { useMeasures } from '../hooks/useMeasures';
import { useDimensionsList } from '../hooks/useDimension';
import { formatFormula } from '../lib/formatFormula';
import { NodeTreeView } from '../components/NodeTreeView';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import type { BuildTreeNode } from '../services/pipelineNode.service';
import type { PipelineNode } from '../services/pipelineNode.service';
import type { Measure } from '../services/measure.service';
import type { Dimension } from '../services/dimension.service';

const includedNodeTypes = ['DIMENSION', 'DATE_DIMENSION', 'MEASURE'];

export default function Diagram() {
  const navigate = useNavigate();
  const isInDraftMode = useIsInDraftMode();
  const [selectedNode, setSelectedNode] = useState<BuildTreeNode | null>(null);

  const { data: buildTreeData, isLoading, error } = useBuildTree({
    includeMeasures: true,
  });

  const { data: nodesData } = usePipelineNodes({ page: 1, pageSize: 1000 });
  const { data: measuresData } = useMeasures({ page: 1, pageSize: 1000 });
  const { data: dimensionsData } = useDimensionsList({ pageSize: 1000 });

  // Build lookup maps
  const nodesById = useMemo(() => {
    const map: Record<string, PipelineNode> = {};
    if (nodesData?.records) {
      for (const node of nodesData.records) {
        map[node.id] = node;
      }
    }
    return map;
  }, [nodesData?.records]);

  const measuresById = useMemo(() => {
    const map: Record<string, Measure> = {};
    if (measuresData?.records) {
      for (const m of measuresData.records) {
        map[m.id] = m;
      }
    }
    return map;
  }, [measuresData?.records]);

  const dimensionsById = useMemo(() => {
    const map: Record<string, Dimension> = {};
    if (dimensionsData?.records) {
      for (const d of dimensionsData.records) {
        map[d.id] = d;
      }
    }
    return map;
  }, [dimensionsData?.records]);

  const filteredNodes = useMemo(() => {
    return buildTreeData?.nodes.filter(
      (node) => includedNodeTypes.includes(node.type)
    ) || [];
  }, [buildTreeData?.nodes]);

  // Filter edges to only those between visible nodes.
  // Reverse NODE_RELATIONSHIP edges (swap from/to) so FK direction shows child -> parent.
  const filteredEdges = useMemo(() => {
    const visibleNodeIds = new Set(filteredNodes.map(n => n.id));
    return (buildTreeData?.edges || [])
      .filter((edge) => visibleNodeIds.has(edge.from_id) && visibleNodeIds.has(edge.to_id))
      .map(e => e.dependency_type === 'NODE_RELATIONSHIP'
        ? { ...e, from_id: e.to_id, to_id: e.from_id }
        : e
      );
  }, [buildTreeData?.edges, filteredNodes]);

  const handleNodeClick = (objectId: string, objectType?: string) => {
    const matchedNode = filteredNodes.find(
      (n) => n.object_id === objectId && n.object_type === objectType
    );
    if (matchedNode) {
      setSelectedNode(matchedNode);
    }
  };

  // Get dimensions for a given entity (pipeline node)
  const entityDimensions = useMemo(() => {
    if (!selectedNode || selectedNode.object_type !== 'PipelineNode') return [];
    const dims = dimensionsData?.records || [];
    return dims
      .filter((d) => d.pipeline_node_id === selectedNode.object_id)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [selectedNode, dimensionsData?.records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading diagram...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">
            Failed to load diagram. Please try again.
          </p>
        </div>
      </div>
    );
  }

  if (filteredNodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <div className="flex flex-col items-center gap-4">
          <Network className="h-12 w-12" />
          <p className="text-lg font-medium">No entities, metrics, or dimensions to display</p>
        </div>
      </div>
    );
  }

  const entity = selectedNode?.object_type === 'PipelineNode'
    ? nodesById[selectedNode.object_id]
    : null;

  const measure = selectedNode?.object_type === 'Measure'
    ? measuresById[selectedNode.object_id]
    : null;

  return (
    <div className="w-full h-full">
      <NodeTreeView
        nodes={filteredNodes}
        edges={filteredEdges}
        onNodeClick={handleNodeClick}
        className="h-full"
      />

      <Sheet open={!!selectedNode} onOpenChange={(open) => {
        if (!open) {
          setTimeout(() => setSelectedNode(null), 300);
        }
      }}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedNode?.title}</SheetTitle>
            <SheetDescription>
              {selectedNode?.object_type === 'PipelineNode' ? 'Entity' : 'Metric'}
            </SheetDescription>
          </SheetHeader>

          {/* Entity detail */}
          {entity && (
            <div className="mt-6 space-y-6">
              <div>
                <h4 className="text-sm font-semibold mb-1">Description</h4>
                <p className={`text-sm ${entity.description ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                  {entity.description || 'No description'}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold mb-2">
                  Dimensions ({entityDimensions.length})
                </h4>
                {entityDimensions.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No dimensions defined</p>
                ) : (
                  <ul className="space-y-1">
                    {entityDimensions.map((dim) => (
                      <li key={dim.id} className="text-sm text-foreground flex items-center justify-between group">
                        <span>{dim.name}</span>
                        {isInDraftMode && (
                          <button
                            onClick={() => navigate(`/develop/dimensions/${dim.id}`)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
                          >
                            <Pencil className="h-3 w-3 text-muted-foreground" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {isInDraftMode && (
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => navigate(`/develop/entities/${entity.id}`)}
                >
                  <Settings className="h-4 w-4" />
                  Configure Entity
                </Button>
              )}
            </div>
          )}

          {/* Metric detail */}
          {measure && (
            <div className="mt-6 space-y-6">
              <div>
                <h4 className="text-sm font-semibold mb-1">Description</h4>
                <p className={`text-sm ${measure.description ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                  {measure.description || 'No description'}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold mb-1">Type</h4>
                <Badge variant="outline">{measure.metric_type}</Badge>
              </div>

              <div>
                <h4 className="text-sm font-semibold mb-1">Formula</h4>
                <p className="text-sm font-mono text-muted-foreground">
                  {formatFormula(measure, nodesById, measuresById, dimensionsById)}
                </p>
              </div>

              {isInDraftMode && (
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => navigate(`/develop/metrics/${measure.id}`)}
                >
                  <Settings className="h-4 w-4" />
                  Configure Metric
                </Button>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
